/**
 * Builds the Phosphor stand-ins that tablerShim.ts exports under tabler's names.
 *
 * vite.config.mts aliases `vue-tabler-icons` to the shim, so the ~200 files
 * that `import { PlusIcon } from 'vue-tabler-icons'` (and every template that
 * uses the globally registered tags) get a Phosphor Regular glyph without a
 * call-site edit. The real package stays reachable as
 * `vue-tabler-icons-original`: any name with no Phosphor mapping is the
 * original tabler component, so nothing ever renders blank.
 */
import { defineComponent, h, type App, type Component } from 'vue';
import * as Tabler from 'vue-tabler-icons-original';
import { TABLER_TO_PHOSPHOR } from './tablerMap';
import { hasPhosphor, phosphorSvg } from './phosphor';

const originals = Tabler as unknown as Record<string, Component>;
const cache = new Map<string, Component>();

/**
 * The component to use for a tabler export name: a Phosphor icon when the name
 * is mapped and the glyph is in the registry, otherwise the original tabler one.
 */
export function tablerIcon(exportName: string): Component {
    const hit = cache.get(exportName);
    if (hit) return hit;
    const ph = TABLER_TO_PHOSPHOR[exportName];
    let component: Component;
    if (!hasPhosphor(ph)) {
        component = originals[exportName];
    } else {
        component = defineComponent({
            name: exportName,
            // Attributes are placed on the <svg> by hand so the inline box
            // styles merge with a caller's `style` instead of being replaced.
            inheritAttrs: false,
            props: {
                // Tabler defaults to 24; BS Labs sizes icons at 16/18/24 and an
                // unsized icon is almost always a button or row icon, so 18.
                size: { type: [Number, String], default: 18 },
                // Accepted so it does not land on the <svg> as an attribute:
                // Phosphor Regular has one fixed weight.
                strokeWidth: { type: [Number, String], default: undefined },
                label: { type: String, default: undefined }
            },
            setup(props, { attrs }) {
                return () =>
                    phosphorSvg(ph, {
                        size: props.size,
                        label: props.label ?? (attrs['aria-label'] as string | undefined),
                        // icon-tabler is kept so existing selectors that position
                        // or colour "the tabler icon" still find this one.
                        class: ['icon-tabler', attrs.class],
                        attrs: { ...attrs, class: undefined }
                    }) ?? h(originals[exportName], attrs)
            }
        });
    }
    cache.set(exportName, component);
    return component;
}

/**
 * Drop-in for `app.use(VueTablerIcons)`: registers every tabler icon globally,
 * as the package's own plugin does, with the mapped names swapped for their
 * Phosphor stand-ins (templates use tags such as <TrashIcon> without an
 * import). Each name is registered once so Vue does not warn about duplicates.
 */
export const TablerPhosphorPlugin = {
    install(app: App) {
        for (const [name, original] of Object.entries(originals)) {
            if (name === 'default') continue;
            app.component(name, TABLER_TO_PHOSPHOR[name] ? tablerIcon(name) : original);
        }
    }
};
